import React from "react";

import { ButtonStandard, Text } from "@performyard/py-components";
import type { CreateMeetingRequestJson, UpdateMeetingRequestJson } from "@performyard/service-contracts";

import ContentModal from "../../../../../../components/Modal/Templates/ContentModal";

import { CalendarErrorType } from "../../../types";

export type CalendarErrorContext =
	| { operation: "create"; meetingData: CreateMeetingRequestJson; workspaceId: string }
	| { operation: "update"; meetingData: UpdateMeetingRequestJson; meetingId: string };

export type CalendarErrorModalState =
	| { type: "none" }
	| {
			type: "error";
			errorType: CalendarErrorType;
			context: CalendarErrorContext;
			isRetrying: boolean;
	  };

interface CalendarErrorModalProps {
	modalState: CalendarErrorModalState;
	onRetry: () => void;
	onContinue: () => void;
	onClose: () => void;
}

// Helper function to determine modal content
const getCalendarErrorModalContent = (errorType: CalendarErrorType) => {
	switch (errorType) {
		case CalendarErrorType.CREATE_FAILED:
			return {
				title: "Failed to Add to Calendar",
				message:
					"Would you like to retry or continue without adding the meeting to your calendar? You are able to add the meeting to your calendar after creating.",
				actions: {
					left: { text: "Retry", color: "primary" as const },
					right: { text: "Continue Without Adding", color: "red" as const },
				},
			};

		case CalendarErrorType.UPDATE_FAILED:
			return {
				title: "Meeting Update Failed",
				message:
					"Would you like to retry or continue without updating the meeting in PerformYard and on your calendar? You can try again at any time.",
				actions: {
					left: { text: "Cancel", color: "primary" as const },
					right: { text: "Retry", color: "green" as const },
				},
			};

		default:
			// Fallback
			return {
				title: "Calendar Error",
				message: "An error occurred with calendar integration. Would you like to retry?",
				actions: {
					left: { text: "Cancel", color: "primary" as const },
					right: { text: "Retry", color: "green" as const },
				},
			};
	}
};

export const CalendarErrorModal = ({ modalState, onRetry, onContinue, onClose }: CalendarErrorModalProps) => {
	if (modalState.type === "none") {
		return null;
	}

	const { title, message, actions } = getCalendarErrorModalContent(modalState.errorType);

	// Determine the correct action handlers based on error type
	const getActionHandlers = () => {
		switch (modalState.errorType) {
			case CalendarErrorType.CREATE_FAILED:
				return {
					leftButton: {
						text: actions.left.text,
						color: actions.left.color,
						onClick: onRetry, // Retry creation
					},
					rightButton: {
						text: actions.right.text,
						color: actions.right.color,
						onClick: onContinue, // Continue without calendar
					},
				};

			case CalendarErrorType.UPDATE_FAILED:
				return {
					leftButton: {
						text: actions.left.text,
						color: actions.left.color,
						onClick: onClose, // Cancel update
					},
					rightButton: {
						text: actions.right.text,
						color: actions.right.color,
						onClick: onRetry, // Retry update
					},
				};

			default:
				// Fallback
				return {
					leftButton: {
						text: "Cancel",
						color: "primary" as const,
						onClick: onClose,
					},
					rightButton: {
						text: "Retry",
						color: "green" as const,
						onClick: onRetry,
					},
				};
		}
	};

	const actionHandlers = getActionHandlers();

	return (
		<ContentModal
			isOpen={true}
			handleClose={modalState.isRetrying ? () => {} : onClose}
			size="large"
			title={
				<Text size="sub-heading" weight="semi-bold">
					{title}
				</Text>
			}
			buttons={[
				<ButtonStandard
					key="left"
					size="large"
					color={actionHandlers.leftButton.color}
					onClick={actionHandlers.leftButton.onClick}
					disabled={modalState.isRetrying}
				>
					{actionHandlers.leftButton.text}
				</ButtonStandard>,
				<ButtonStandard
					key="right"
					size="large"
					color={actionHandlers.rightButton.color}
					onClick={actionHandlers.rightButton.onClick}
					disabled={modalState.isRetrying}
				>
					{actionHandlers.rightButton.text}
				</ButtonStandard>,
			]}
		>
			<div className="text-center">
				<Text size="paragraph-1" weight="regular">
					{message}
				</Text>
			</div>
		</ContentModal>
	);
};
